import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../types';
import { apiClient } from '../../services/apiClient';
import './SelectionModal.css';

interface CustomerSelectionModalProps {
  onSelect: (customer: Customer) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const CustomerSelectionModal: React.FC<CustomerSelectionModalProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const searchCustomers = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setCustomers([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.searchCustomers(query);
      setCustomers(data);
      setHasSearched(true);
    } catch (err) {
      setError(t('messages.errorLoadingData'));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      searchCustomers(searchQuery);
    }, DEBOUNCE_MS);

    // Cleanup on unmount or query change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, searchCustomers]);

  const handleSelect = (customer: Customer) => {
    onSelect(customer);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('reportForm.selectCustomer')}</h3>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          <input
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="filter-input"
            autoFocus
          />

          <div className="search-results-container">
            {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_LENGTH && (
              <div className="search-hint">
                {t('reportForm.minSearchCharsCustomer', { count: MIN_SEARCH_LENGTH })}
              </div>
            )}

            {loading && <div className="loading">{t('app.loading')}</div>}

            {error && <div className="error-message">{error}</div>}

            {!loading && !error && searchQuery.length === 0 && (
              <div className="search-hint">
                {t('reportForm.typeToSearchCustomer')}
              </div>
            )}

            {!loading && !error && hasSearched && (
              <div className="selection-list">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="selection-item"
                    onClick={() => handleSelect(customer)}
                  >
                    <div className="item-main">{customer.name}</div>
                    <div className="item-details">
                      {customer.city}{customer.countryCode ? `, ${customer.countryCode}` : ''}
                    </div>
                    {customer.email && <div className="item-details">{customer.email}</div>}
                  </div>
                ))}
                {customers.length === 0 && (
                  <div className="no-results">{t('mainForm.noResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectionModal;
